'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

import type { ApiRequestEntry } from '../types';
import { registerFetchInterceptor } from '../interceptors/fetch-interceptor';

const MAX_ENTRIES = 100;

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}

interface ApiRequestCaptureContextValue {
  getApiRequests: () => ApiRequestEntry[];
}

const ApiRequestCaptureContext =
  createContext<ApiRequestCaptureContextValue | null>(null);

export function ApiRequestCaptureProvider({ children }: { children: ReactNode }) {
  const entriesRef = useRef<ApiRequestEntry[]>([]);

  const getApiRequests = useCallback(
    (): ApiRequestEntry[] => [...entriesRef.current],
    [],
  );

  const handleCapture = useCallback(
    (entry: Omit<ApiRequestEntry, 'id'>): void => {
      const entries = entriesRef.current;
      if (entries.length >= MAX_ENTRIES) entries.shift();
      entries.push({ ...entry, id: generateId() });
    },
    [],
  );

  useEffect(() => {
    return registerFetchInterceptor({ onCapture: handleCapture });
  }, [handleCapture]);

  return (
    <ApiRequestCaptureContext.Provider value={{ getApiRequests }}>
      {children}
    </ApiRequestCaptureContext.Provider>
  );
}

export function useApiRequestCapture(): ApiRequestCaptureContextValue {
  const context = useContext(ApiRequestCaptureContext);
  if (!context) {
    throw new Error(
      'useApiRequestCapture must be used within an ApiRequestCaptureProvider',
    );
  }
  return context;
}
